import * as React from 'react';
import ContentContainer from 'nomst/Components/SharedJobCollab/contentContainer';
import CompFactorsDisplay from 'nomst/Components/JobDetails/compFactorsDisplay';
import DetailsSection from 'nomst/Components/SharedJobCollab/detailsSection';
import { DetailsCard } from 'nomst/Components/SharedJobCollab/detailsCard';
import JobDetailsSummarySection from 'nomst/Components/JobDetails/jobDetailsSummarySection';
import JobMatchSection from 'nomst/Components/JobDetails/jobMatchSection';
import GradeRangeVisual from 'nomst/Components/JobDetails/gradeRangeVisual';
import LocationPicker from 'nomst/Components/JobDetails/locationPicker';
import Spinner from 'components/Common/Spinner';
import SkillsAndCertificationsSection from 'nomst/Components/JobDetails/skillsAndCertsSection';
import { mxStrings, compNames } from 'utils/JobCollabStrings';
import { jobStore, navigateToNoMst } from 'utils/MobxDataManager/singletons';
import { observer } from 'mobx-react';
import './jobDetailsView.scss';

const defaultJobLocations = [{ label: 'Xxxxxxxxxx, Xx' } as JobLocation];

@observer
class JobDetailsView extends React.PureComponent {

    render(): React.ReactNode {
        const { jobDetails, isLoadingJobDetails } = jobStore;

        let noDetailsMessage;
        if (!isLoadingJobDetails && (!jobDetails || Object.keys(jobDetails).length === 0)) {
            noDetailsMessage = <ContentContainer>
                <div className="no-details-message">
                    {mxStrings.noJobDetails}
                </div>
            </ContentContainer>;
        }

        return (
            <div className="view job-details-view">
                { noDetailsMessage || this._renderDetailsCard() }
            </div>
        );
    }

    private _renderDetailsCard(): React.ReactNode {
        const { jobDetails, isLoadingJobDetails } = jobStore;
        const placeholder = isLoadingJobDetails;
        const className = `job-details-content ${placeholder ? 'placeholder' : ''}`;

        return (
            <div className={className}>
                <ContentContainer>
                    <div className="job-title-header">
                        <div className="job-title-header-inner">
                            <div className="job-title-header-text">
                                <span>{ jobDetails?.orgJobTitle ?? '' }</span>
                            </div>
                        </div>
                        { this._renderDetailsSummarySection(jobDetails) }
                    </div>
                    { this._renderLocationPicker() }
                </ContentContainer>
                <ContentContainer nopadding={true}>
                    <DetailsSection className="pay-range-container">
                        { this._renderMarketMedian(jobDetails) }
                        <DetailsCard>
                            <GradeRangeVisual
                                gradeMinimum={jobDetails?.gradeMinimum}
                                gradeMidpoint={jobDetails?.gradeMidpoint}
                                gradeMaximum={jobDetails?.gradeMaximum}
                                currency={jobDetails?.currency}
                                structureHourly={jobDetails?.structureHourly}
                            />
                        </DetailsCard>
                    </DetailsSection>
                </ContentContainer>
                <ContentContainer nopadding={true}>
                    { this._renderDetailsMatchSection(jobDetails) }
                    { !placeholder && this._renderCompFactors() }
                    { !placeholder && this._renderSkillsAndCerts() }
                </ContentContainer>
            </div>
        );
    }

    private _renderCompFactors(): React.ReactNode {
        const { jobDetails } = jobStore;
        if (!jobDetails?.payscaleJobInfo) {
            return null;
        }

        const shouldDisplayAsCompFactor = (cfKey: string): boolean => {
            switch (cfKey) {
                case compNames.skills:
                case compNames.certifications:
                case compNames.tasks:
                case compNames.jobResponsibilities:
                    return false;
            }

            return true;
        };

        // filter comp factors to not show skills/certs/tasks as those are shown elsewhere
        const compFactorKeys = Object.keys(jobDetails.payscaleJobInfo).filter(shouldDisplayAsCompFactor);
        if (compFactorKeys.length === 0) {
            return null;
        }

        const compFactors: {[key: string]: CompfactorValue} = {};
        compFactorKeys.forEach(key => {
            const value = jobDetails.payscaleJobInfo ? jobDetails.payscaleJobInfo[key] : '';
            compFactors[key] = value;
        });

        const jobResponsibilities = jobDetails.payscaleJobInfo[compNames.jobResponsibilities] as string[];

        return <CompFactorsDisplay
            compFactors={compFactors}
            jobResponsibilities={jobResponsibilities}
        />;
    }

    private _renderSkillsAndCerts(): React.ReactNode {
        const { jobDetails } = jobStore;

        let skills: Answer[] = [];
        let certs: Answer[] = [];
        if (jobDetails?.payscaleJobInfo) {
            skills = jobDetails.payscaleJobInfo[compNames.skills] as Answer[];
            certs = jobDetails.payscaleJobInfo[compNames.certifications] as Answer[];
        }

        return (
            <SkillsAndCertificationsSection
                skills={skills}
                certifications={certs}
            />
        );
    }

    private _renderDetailsSummarySection(details: Nullable<JobDetails>): React.ReactNode {
        let yearsExperience: CompfactorValue = '';
        if (details?.payscaleJobInfo) {
            yearsExperience = details.payscaleJobInfo[compNames.yearsExperience];
        }

        return <JobDetailsSummarySection
            employees={details?.numberOfEmployees}
            salaryGrade={details?.grade}
            yearsExperience={yearsExperience}
        />;
    }

    private _renderDetailsMatchSection(details:  Nullable<JobDetails>): React.ReactNode {
        const tasks = details?.payscaleJobInfo ? details.payscaleJobInfo[compNames.tasks] as string[] : null;
        return <JobMatchSection
            payscaleJobTitle={details?.payscaleJobTitle}
            jobTasks={tasks}
            fullSection={true}
        />;
    }

    private _getDisplayValue(currency: string, marketBaseSalary50th: number, structureHourly: boolean): JSX.Element {
        const medianValue = structureHourly ?
            marketBaseSalary50th / 2080 : marketBaseSalary50th;
        
        return medianValue ?
            <>
                <span>{jobStore.formatRangeValue(medianValue, currency)}</span>
                { structureHourly ? mxStrings.perHour : '' }
            </> :
            <span>{mxStrings.unspecifiedValue}</span>;
    }

    private _renderLocationPicker(): React.ReactNode {
        const { jobDetails, jobLocations } = jobStore;

        let locations: JobLocation[] = [];
        if (!jobDetails) {
            // We don't have any data yet, so use the placeholder locations
            locations = defaultJobLocations;
        } else if (jobDetails && !jobLocations.length) {
            // We have data, but locations are loading, so pull what we can
            // from jobDetails
            locations = [{
                label: jobDetails.orgJobCodeKey || jobDetails.workLocation || '',
                orgJobId: jobDetails.orgJobId || 0
            }];
        } else {
            // We have everything
            locations = jobLocations;
        }

        return <div className="location-picker-container">
            <div className="location-picker-body">
                <label className="location-picker-title pxl-small" htmlFor="locationPicker">
                    { mxStrings.jobDetailsLocationHeader }
                </label>
                {jobDetails && !jobLocations.length && <Spinner size={12} />}
                <LocationPicker
                    locations={locations}
                    value={Number(jobStore.selectedJobId)}
                    onLocationSelect={(jobId: string) => this._handleLocationSelect(jobId)}
                />
            </div>
        </div>;
    }

    private _handleLocationSelect(jobId: string): Promise<void> {
        const { jobDetails } = jobStore;
        const jobCode = jobDetails?.orgJobCode ?? null;
        if (!jobCode) {
            return Promise.resolve();
        }

        jobStore.setSelectedJobId(jobId);
        jobStore.clearJobDetails();
        navigateToNoMst.navigateToJobDetails(jobCode, jobId);
        return jobStore.fetchJobDetails();
    }

    private _renderMarketMedian(details:  Nullable<JobDetails>): React.ReactNode {
        const medianPay = this._getDisplayValue(
            details?.currency || 'USD',
            details?.marketBaseSalary50Th || 0,
            !!details?.structureHourly);

        return <DetailsCard>
            <div className="pxl-small estimated-median-pay-header">
                { mxStrings.jobDetailsBasePayHeader }
            </div>
            <div className="estimated-median-pay-body">
                { medianPay }
            </div>
        </DetailsCard>;
    }
}

export { JobDetailsView };
export default JobDetailsView;
